import { rowScalar } from '../../encounter-map/ft3d/perception';
import { itemsCol } from '../../shared/array-types';
import type { PresentationContext } from './ft3d';
export type VoteChoice = 'yes' | 'no' | 'abstain';
export type VoteResult = 'yes' | 'no' | 'divided' | 'abstain';
export interface VoteBallot { playerCharacterId: string; choice: VoteChoice; at: number; version?: string }
export interface VotePoll {
    pollId: string; status: 'open' | 'closed'; openedAt: number; closesAt: number; closedAt: number;
    result: VoteResult | null; eligible: Array<{ playerCharacterId: string; displayName: string }>;
    ballots: VoteBallot[]; encounterId?: string;
}
interface VoteBase extends Record<string, unknown> {
    type: 'ft3d_party_vote'; schema: 'ft.ft3d.party-vote.v1'; campaign_id: string; encounter_id: string;
    actor_id: string; operation_id: string; transport_scope: 'campaign';
    now: number;
    cues?: Record<string, string>;
}
type PollIdentity = Pick<VotePoll, 'pollId' | 'openedAt'>;
export type VoteMessage = VoteBase & (
    { reason: 'open'; poll: VotePoll }
    | { reason: 'cast'; poll: PollIdentity; ballot: VoteBallot }
    | { reason: 'close'; poll: PollIdentity & Pick<VotePoll, 'closedAt' | 'result' | 'encounterId'> & { status: 'closed' } }
);
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
export function voteRoster(ctx: PresentationContext): VotePoll['eligible'] {
    return (ctx.campaign?.campaign_players ?? []).filter((row: Record<string, unknown>) => Number(rowScalar(row, 'player_character_inactive') ?? 0) !== 1)
        .map((row: Record<string, unknown>) => ({ playerCharacterId: upper(itemsCol(row, 'item_id')),
            displayName: String(rowScalar(row, 'player_name') || itemsCol(row, 'item_name') || 'Player').slice(0, 80) }))
        .filter((row: VotePoll['eligible'][number]) => row.playerCharacterId);
}
export function voteActor(ctx: PresentationContext, poll: VotePoll, preferred = ctx.actorId): string {
    const rows = ctx.encounter?.encounter_creatures_arr ?? [];
    const owned = rows.filter((row: Record<string, unknown>) => {
        const pcId = upper(rowScalar(row, 'item_id') || itemsCol(row, 'item_id'));
        const roster = ctx.campaign?.campaign_players?.find((entry: Record<string, unknown>) => upper(itemsCol(entry, 'item_id')) === pcId);
        return (rowScalar(row, 'item_type') || itemsCol(row, 'item_type')) === 'player_character' && poll.eligible.some(entry => upper(entry.playerCharacterId) === pcId)
            && roster && ['player_character_created_by_id', 'permissioned'].some(key => upper(rowScalar(roster, key)) === upper(ctx.userId));
    });
    return upper(rowScalar(owned.find((row: Record<string, unknown>) => upper(rowScalar(row, 'encounter_creature_id')) === upper(preferred)) ?? owned[0] ?? {}, 'encounter_creature_id'));
}
export function voteResult(ballots: readonly VoteBallot[]): VoteResult {
    const yes = ballots.filter(row => row.choice === 'yes').length, no = ballots.filter(row => row.choice === 'no').length;
    return !yes && !no ? 'abstain' : yes > no ? 'yes' : no > yes ? 'no' : 'divided';
}
export function voteComplete(poll: VotePoll): boolean {
    return poll.eligible.length > 0 && poll.eligible.every(entry => poll.ballots.some(ballot => upper(ballot.playerCharacterId) === upper(entry.playerCharacterId)));
}
/** Merge only a changed seat. A concurrent ballot never overwrites another voter's choice. */
export function mergeVote(poll: VotePoll | null, message: VoteMessage): VotePoll | null {
    const incoming = message.poll;
    if (!poll || poll.pollId !== incoming.pollId) {
        if (poll && (poll.openedAt > incoming.openedAt || (poll.openedAt === incoming.openedAt && poll.pollId > incoming.pollId))) return poll;
        if (message.reason !== 'open') return poll;
        return { ...message.poll, ballots: message.poll.ballots.slice() };
    }
    if (poll.status === 'closed') return poll;
    if (message.reason === 'close') return { ...poll, status: 'closed', closedAt: message.poll.closedAt, result: message.poll.result };
    if (message.reason !== 'cast') return poll;
    const ballot = message.ballot;
    const index = poll.ballots.findIndex(row => upper(row.playerCharacterId) === upper(ballot.playerCharacterId));
    const previous = poll.ballots[index];
    if (previous && (previous.at > ballot.at || (previous.at === ballot.at && String(previous.version) >= String(ballot.version)))) return poll;
    if (index < 0) poll.ballots.push(ballot); else poll.ballots[index] = ballot;
    return poll;
}
export function decideVote(ctx: PresentationContext, poll: VotePoll | null, body: Record<string, unknown>, now: number): VoteMessage {
    const action = String(body.party_vote_action);
    const base = { type: 'ft3d_party_vote' as const, schema: 'ft.ft3d.party-vote.v1' as const,
        campaign_id: ctx.campaignId, encounter_id: ctx.encounterId, actor_id: '',
        transport_scope: 'campaign' as const, operation_id: crypto.randomUUID(), now };
    if (action === 'open') {
        if (!ctx.canManage) throw new Error('Only the GM can call a party vote.');
        if (poll?.status === 'open' && poll.closesAt > now) throw new Error('A party vote is already open.');
        const eligible = voteRoster(ctx);
        if (!eligible.length) throw new Error('No active player characters to poll.');
        const seconds = Math.max(15, Math.min(600, Number(body.party_vote_seconds) || 60));
        return { ...base, reason: 'open', poll: { pollId: crypto.randomUUID().toUpperCase(), status: 'open',
            openedAt: now, closesAt: now + seconds, closedAt: 0, result: null, eligible, ballots: [], encounterId: ctx.encounterId } };
    }
    if (!poll || poll.status !== 'open') throw new Error('There is no open party vote.');
    if (String(body.party_vote_poll_id) !== poll.pollId) throw new Error('That action was for an earlier poll.');
    if (action === 'cast') {
        if (poll.closesAt <= now) throw new Error('The party vote has ended.');
        const actorId = String(body.party_vote_actor_id);
        if (!actorId || voteActor(ctx, poll, actorId) !== upper(actorId)) throw new Error('That character is not yours to vote with.');
        const actor = ctx.encounter!.encounter_creatures_arr.find((row: Record<string, unknown>) => upper(rowScalar(row, 'encounter_creature_id')) === upper(actorId));
        const choice = body.party_vote_choice as VoteChoice;
        if (!['yes', 'no', 'abstain'].includes(choice)) throw new Error('A vote must be yes, no, or abstain.');
        return { ...base, reason: 'cast', actor_id: actorId, poll: { pollId: poll.pollId, openedAt: poll.openedAt },
            ballot: { playerCharacterId: upper(rowScalar(actor, 'item_id') || itemsCol(actor, 'item_id')), choice, at: now, version: base.operation_id } };
    }
    if (action !== 'close') throw new Error('Unknown party vote action.');
    if (!ctx.canManage && !voteComplete(poll) && poll.closesAt > now) throw new Error('Only the GM can close a party vote early.');
    return { ...base, reason: 'close', operation_id: `${poll.pollId}:close`,
        poll: { pollId: poll.pollId, openedAt: poll.openedAt, encounterId: poll.encounterId,
            status: 'closed', closedAt: now, result: voteResult(poll.ballots) } };
}
